import React, { useMemo } from "react";
import Highcharts from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import { Box, Stack, Typography } from "@mui/material";
import PeriodSelect from "./PeriodSelect";
import WidgetShell from "./WidgetShell";
import { ENTERPRISE_CHART_COLORS, HC_CONTAINER_PROPS } from "./chartTheme";
import { EXD, ExdEmpty, chartWrapSx } from "./exdMui";

const UserLoginStatsWidget = ({
  title,
  infoTitle,
  loading,
  chartOptions,
  periodValue,
  onPeriodChange,
  periodFallback = "All",
}) => {
  const points = useMemo(() => {
    const raw = chartOptions?.series?.[0]?.data;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((point, index) => {
        if (point && typeof point === "object" && !Array.isArray(point)) {
          return {
            name: point.name || `Item ${index + 1}`,
            y: Number(point.y) || 0,
            color:
              ENTERPRISE_CHART_COLORS[index % ENTERPRISE_CHART_COLORS.length],
          };
        }
        return {
          name: `Item ${index + 1}`,
          y: Number(point) || 0,
          color: ENTERPRISE_CHART_COLORS[index % ENTERPRISE_CHART_COLORS.length],
        };
      })
      .sort((a, b) => b.y - a.y);
  }, [chartOptions]);

  const total = useMemo(
    () => points.reduce((sum, p) => sum + (Number(p.y) || 0), 0),
    [points],
  );

  const barOptions = useMemo(() => {
    if (!points.length) return null;

    return {
      chart: {
        type: "bar",
        height: Math.max(180, points.length * 34 + 36),
        backgroundColor: "transparent",
        spacing: [8, 8, 8, 4],
        style: { fontFamily: EXD.font },
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        backgroundColor: "#161a3a",
        borderColor: "#161a3a",
        borderRadius: 8,
        outside: true,
        headerFormat: "",
        style: { color: "#fff", fontSize: "12px" },
        pointFormatter() {
          return `<b>${this.y.toLocaleString()} logins</b>`;
        },
        useHTML: true,
      },
      xAxis: {
        categories: points.map((p) => p.name),
        lineColor: EXD.border,
        tickLength: 0,
        labels: {
          style: { color: EXD.muted, fontSize: "12px", fontWeight: "600" },
        },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: EXD.border,
        labels: { style: { color: EXD.muted, fontSize: "11px" } },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderWidth: 0,
          pointWidth: 14,
          colorByPoint: true,
          dataLabels: { enabled: false },
        },
      },
      colors: ENTERPRISE_CHART_COLORS,
      series: [
        {
          type: "bar",
          name: "Logins",
          data: points.map((p) => ({ y: p.y, color: p.color })),
        },
      ],
    };
  }, [points]);

  return (
    <WidgetShell
      title={title}
      infoTitle={infoTitle}
      loading={loading}
      controls={
        onPeriodChange ? (
          <PeriodSelect
            value={periodValue}
            onChange={onPeriodChange}
            fallbackLabel={periodFallback}
          />
        ) : null
      }
    >
      {barOptions ? (
        <Box sx={chartWrapSx(180)}>
          <HighchartsReact
            highcharts={Highcharts}
            options={barOptions}
            containerProps={HC_CONTAINER_PROPS}
          />
        </Box>
      ) : (
        <ExdEmpty />
      )}
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{
          borderTop: `1px dashed ${EXD.border}`,
          mt: 0.5,
          pt: 1.25,
        }}
      >
        <Typography sx={{ color: EXD.muted, fontSize: 12.5, fontWeight: 600 }}>
          Total logins
        </Typography>
        <Typography
          sx={{
            color: EXD.text,
            fontFamily: EXD.font,
            fontSize: 15,
            fontVariantNumeric: "tabular-nums",
            fontWeight: 700,
          }}
        >
          {total ? total.toLocaleString() : "0"}
        </Typography>
      </Stack>
    </WidgetShell>
  );
};

export default UserLoginStatsWidget;
